import DynamicLeadForm from "@components/LeadForm/DynamicLeadForm";
import { LeadFormTypes } from "@components/LeadForm/enums";
import { Paper, Stack } from "@mantine/core";
import useLeadFormStore from "stores/leadFormStore";
import { useEffect, useState } from "react";

type NumberInputCardProps = {
  isds: any;
  leadFormType: string;
  stripText: string;
  stripIcon: string;
}

export default function NumberInputCard({ isds, leadFormType, stripText, stripIcon }: NumberInputCardProps) {
  const [mounted, setMounted] = useState(false);
  const {
    leadFormHeadingHealth,
    leadFormHeadingTravel,
    leadFormHeadingCorporate,
    leadFormOfferHealth,
    leadFormOfferTravel,
    leadFormOfferCorporate,
    isHydrated,
  } = useLeadFormStore();

  // Handle hydration
  useEffect(() => {
    setMounted(true);
  }, []);

  // Wait for both component mount and store hydration
  const isReady = mounted && isHydrated;

  // Determine heading: use store value if available, otherwise use default
  const getHeading = () => {
    if (!isReady) {
      // Return default heading during SSR/initial render
      return getDefaultHeading(leadFormType);
    }

    if ((leadFormType === LeadFormTypes.SINGLE || leadFormType === LeadFormTypes.TWO || leadFormType === LeadFormTypes.THREE || leadFormType === LeadFormTypes.FIVE) && leadFormHeadingHealth) {
      return leadFormHeadingHealth;
    } else if (leadFormType === LeadFormTypes.TRAVEL && leadFormHeadingTravel) {
      return leadFormHeadingTravel;
    } else if (leadFormType === LeadFormTypes.CORPORATE && leadFormHeadingCorporate) {
      return leadFormHeadingCorporate;
    }
    
    return getDefaultHeading(leadFormType);
  };

  // Determine offer text: use store value if available, otherwise use default
  const getOffer = () => {
    if (!isReady) {
      // Return default offer during SSR/initial render
      return getDefaultOffer(leadFormType);
    }

    if ((leadFormType === LeadFormTypes.SINGLE || leadFormType === LeadFormTypes.TWO || leadFormType === LeadFormTypes.THREE || leadFormType === LeadFormTypes.FIVE) && leadFormOfferHealth) {
      return leadFormOfferHealth;
    } else if (leadFormType === LeadFormTypes.TRAVEL && leadFormOfferTravel) {
      return leadFormOfferTravel;
    } else if (leadFormType === LeadFormTypes.CORPORATE && leadFormOfferCorporate) {
      return leadFormOfferCorporate;
    }
    
    return getDefaultOffer(leadFormType);
  };

  const heading = getHeading();
  const offer = getOffer();

  // Determine strip text
  let displayStripText = stripText || "";

  const domainMap: Record<string, string | null> = {
    LOCAL: process.env.DOMAIN_LOCAL || null,
    UAT: process.env.DOMAIN_UAT || null,
    PROD: process.env.DOMAIN_PROD || null,
  };
  const env = (process.env.MONGO_CONFIG || "PROD").toUpperCase();
  const domain = domainMap[env] || null;
  const imageUrl = `https://${domain}${stripIcon}`;
  console.log({stripText, stripIcon});
  return (
    <Paper h={"fit-content"} shadow="xl" radius="md" withBorder>
      <div className="bg-nivabupa-blue-ethinosPlanRecommendation h-14 flex justify-center items-center font-bold text-lg rounded-t-md text-white">
        {heading}
      </div>
      {displayStripText && (
        <div className='bg-[#ffaf1433] text-center p-2 text-sm flex flex-row items-center justify-center prose-p:text-sm gap-1'>
          <img height={24} width={24} src={imageUrl} alt='strip icon' loading='lazy' />
          {displayStripText}
        </div>
      )}
      <Stack p="xl">
        <p>{offer}</p>
        <div className="flex flex-col gap-4 mb-2 ">
          <div className="flex flex-row items-center relative w-full">
            <DynamicLeadForm isds={isds} leadFormType={leadFormType} />
          </div>
        </div>
      </Stack>
    </Paper>
  );
}

// Helper function for default headings
function getDefaultHeading(leadFormType: string): string {
  switch (leadFormType) {
    case LeadFormTypes.TRAVEL:
      return "Secure Your Trip in Minutes";
    case LeadFormTypes.CORPORATE:
      return "Get Group Coverage for Your Company";
    case LeadFormTypes.SINGLE:
    case LeadFormTypes.TWO:
    case LeadFormTypes.THREE:
    case LeadFormTypes.FIVE:
    default:
      return "Start Your Health Insurance Today";
  }
}

// Helper function for default offers
function getDefaultOffer(leadFormType: string): string {
  switch (leadFormType) {
    case LeadFormTypes.TRAVEL:
      return "Stay protected against medical emergencies, trip delays, and lost baggage worldwide.";
    case LeadFormTypes.CORPORATE:
      return "Get flexible corporate insurance policies tailored to your company's unique needs.";
    case LeadFormTypes.SINGLE:
    case LeadFormTypes.TWO:
    case LeadFormTypes.THREE:
    case LeadFormTypes.FIVE:
    default:
      return "Get right coverage, right premium and the right protection instantly.";
  }
}